import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { getEnabledLanguages, getSiteContent } from "../httpService.js";
import { getSnapshotValue, hasSnapshotValue } from "../snapshot.js";
import { UserContext } from "./UserContext";

const SiteContentContext = React.createContext({});

const SiteContentProvider = (props) => {
  const [content, setContent] = useState(() => getSnapshotValue("siteContent", []));
  const [languages, setLanguages] = useState(() => getSnapshotValue("languages", []));
  const [overlayActive, setOverlayActive] = useState(false);
  // Prerendered pages ship the content inline, so background media can render immediately
  // instead of waiting for the fetch that would otherwise gate it.
  const [contentLoaded, setContentLoaded] = useState(() => hasSnapshotValue("siteContent"));

  const { locale } = useContext(UserContext);

  const fetchContent = useCallback(async (lang) => {
    try {
      const data = await getSiteContent(lang);
      setContent(data);
    } catch {
      // Site content fetch failed silently — page still renders with defaults
    } finally {
      setContentLoaded(true);
    }
  }, []);

  const fetchLanguages = useCallback(async () => {
    try {
      const data = await getEnabledLanguages();
      setLanguages(data);
    } catch {
      // Fall back to empty — header will use defaults
    }
  }, []);

  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  useEffect(() => {
    fetchContent(locale);
  }, [fetchContent, locale]);

  const contentMap = useMemo(() => new Map(content.map((c) => [c.contentKey, c.value])), [content]);

  const colorMap = useMemo(
    () => new Map(content.filter((c) => c.color).map((c) => [c.contentKey, c.color])),
    [content]
  );

  const getContent = useCallback(
    (key, fallback = "") => contentMap.get(key) || fallback,
    [contentMap]
  );

  const getContentColor = useCallback((key) => colorMap.get(key) || null, [colorMap]);

  const value = useMemo(
    () => ({
      content,
      getContent,
      getContentColor,
      locale,
      languages,
      overlayActive,
      setOverlayActive,
      contentLoaded,
      reinitialize: () => fetchContent(locale),
    }),
    [
      content,
      getContent,
      getContentColor,
      locale,
      languages,
      overlayActive,
      contentLoaded,
      fetchContent,
    ]
  );

  return <SiteContentContext.Provider value={value} {...props} />;
};

export { SiteContentProvider, SiteContentContext };
